import React, { Component } from "react";
import { Button, Form, Input, notification, Select, Space, Spin, Avatar, Modal } from "antd";
import { FormattedMessage, injectIntl } from "react-intl";
import styled, { withTheme } from "styled-components";
import AppContext from "../../../AppContext";
import PasswordCriteria, { validatePassword } from "../../feedback/passwordCompliance";

class UpdatePassword extends Component {
  static contextType = AppContext;

  passwordChange = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      saving: false,
      show: false,
      newPass: "",
      confirmPass: "",
      filled: false,
      passwordEditing: false,
      securityAnswer: "",
    };
  }

  componentDidUpdate = prevProps => {
    if (this.props.reset !== prevProps.reset && this.props.reset && this.passwordChange.current?.resetFields) {
      this.passwordChange.current.resetFields();
      this.setState({
        saving: false,
        show: false,
        newPass: "",
        confirmPass: "",
        filled: false,
        passwordEditing: false,
        securityAnswer: "",
      });
    }
  };

  checkAllFields = changedFields => {
    const fields = this.passwordChange.current?.getFieldsValue();
    const keys = Object.keys(fields ?? {});
    let passwords = {
      current_password: undefined,
      new_password: undefined,
      confirm_new_password: undefined,
    };
    let isEditing = false;
    for (let i = 0; i < keys?.length; i++) {
      if (Object.keys(passwords).includes(keys?.[i])) {
        passwords[keys[i]] = fields?.[keys?.[i]];
        if (!isEditing && (fields?.[keys?.[i]] ?? "") !== "") {
          this.setState({ passwordEditing: true });
        }
        isEditing = true;
      }
    }
    let reset =
      (passwords.current_password === "" || passwords.current_password === undefined) &&
      (passwords.new_password === "" || passwords.new_password === undefined) &&
      (passwords.confirm_new_password === "" || passwords.confirm_new_password === undefined);
    let showEverything =
      passwords.current_password !== undefined &&
      passwords.new_password !== undefined &&
      passwords.confirm_new_password !== undefined;
    if (!reset) {
      if (this.state.show !== showEverything) {
        this.setState({ show: showEverything });
        if (!showEverything && this.state.filled) {
          this.setState({ filled: false });
        }
      }
      let isFilled = this.checkChallenge();
      if (showEverything && isFilled && this.checkNoErrors()) {
        this.setState({ filled: isFilled });
      } else {
        this.setState({ filled: false });
      }
    }
    if (reset && this.passwordChange.current?.resetFields) {
      this.passwordChange.current.resetFields();
      if (this.state.show) {
        this.setState({ show: false });
      }
      if (this.state.filled) {
        this.setState({ filled: false });
      }
      this.setState({ passwordEditing: false });
    }
  };

  checkChallenge = () => {
    const fields = this.passwordChange.current?.getFieldsValue();
    if (this.state.show) {
      if ((fields?.security_question ?? "") !== "") {
        return true;
      }
    }
    return false;
  };

  checkNoErrors = () => {
    const errors = this.passwordChange.current?.getFieldsError() ?? [];
    if (this.state.securityAnswer === "") {
      return false;
    }
    for (let i = 0; i < errors.length; i++) {
      if ((errors?.[i]?.errors?.length ?? 0) > 0) {
        return false;
      }
    }
    return true;
  };

  onSubmit = async () => {
    if (this.checkNoErrors()) {
      this.setState({ saving: true });
      let results = await this.props.changePassword({ ...this.passwordChange.current?.getFieldsValue() });
      if (results === true) {
        if (this.passwordChange.current?.resetFields) {
          this.passwordChange.current.resetFields();
        }
        notification.success({
          message: <div className="text-capitalize-first">{this.props.intl.formatMessage({ id: "success" })}</div>,
          duration: 5,
        });
        this.setState({ filled: false, show: false, saving: false, newPass: "", confirmPass: "", securityAnswer: "" });
        this.setState({ passwordEditing: false });
      } else {
        this.setState({
          saving: false,
        });
        notification.error({
          message: (
            <div className="text-capitalize-first">
              {this.props.intl.formatMessage({
                id: "there was a problem with your current password or authorisation.",
              })}
            </div>
          ),
          duration: 5,
        });
      }
    } else {
      this.setState({
        saving: false,
      });
      notification.error({
        message: (
          <div className="text-capitalize-first">
            {this.props.intl.formatMessage({
              id: "there was a problem with your current password or authorisation.",
            })}
          </div>
        ),
        duration: 5,
      });
    }
  };

  checkPassword = async (rule, value) => {
    if (this.passwordChange.current?.validateFields) {
      this.passwordChange.current.validateFields(["confirm_new_password"]);
    }
    if (rule?.required) {
      if (validatePassword(value)) {
        return Promise.resolve();
      } else {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  comparePasswords = async (rule, value) => {
    if (rule?.required) {
      if (value === this.state.newPass) {
        return Promise.resolve();
      } else {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  getQuestions = () => {
    return (this.props.questions ?? []).map(question =>
      Object.fromEntries(
        new Map([
          ["label", this.props.intl.formatMessage({ id: question })],
          ["value", question],
        ])
      )
    );
  };

  onCancel = () => {
    const { confirm } = Modal;
    const { intl } = this.props;
    confirm({
      width: 500,
      title: (
        <div className="text-capitalize-first">
          {intl.formatMessage({ id: "are you sure you want to cancel these changes?" })}
        </div>
      ),
      content: (
        <div className="text-capitalize-first">
          {intl.formatMessage({ id: "if you continue, changes will be lost." })}
        </div>
      ),
      okText: <div className="text-capitalize-first">{intl.formatMessage({ id: "yes" })}</div>,
      okButtonProps: { type: "primary" },
      cancelText: <div className="text-capitalize-first">{intl.formatMessage({ id: "no" })}</div>,
      cancelButtonProps: { type: "default" },
      onOk: async () => {
        if (this.passwordChange.current?.resetFields) {
          this.passwordChange.current.resetFields();
          await this.setState({
            passwordEditing: false,
            filled: false,
            show: false,
            saving: false,
            newPass: "",
            confirmPass: "",
            securityAnswer: "",
          });
        }
      },
    });
  };

  getSecurityQuestions = () => {
    return (
      <>
        <Form.Item
          name="security_question"
          label={
            <span className="text-capitalize">
              <FormattedMessage id="security question" />
            </span>
          }
          rules={[
            {
              required: true,
              message: (
                <div className="text-capitalize-first">{this.props.intl.formatMessage({ id: "select question" })}</div>
              ),
            },
          ]}
        >
          <Select
            options={this.getQuestions()}
            allowClear
            getPopupContainer={e => e.parentElement}
            placeholder={
              <div className="text-capitalize-first">
                {this.props.intl.formatMessage({ id: "select security question" })}
              </div>
            }
          />
        </Form.Item>
        <Form.Item
          name="security_answer"
          label={
            <span className="text-capitalize">
              <FormattedMessage id="response" />
            </span>
          }
          rules={[
            {
              required: true,
              message: (
                <div className="text-capitalize-first">
                  {this.props.intl.formatMessage({ id: "enter security question response" })}
                </div>
              ),
            },
          ]}
        >
          <Input
            onChange={e => this.setState({ securityAnswer: e?.target?.value ?? "" })}
            placeholder={this.props.intl.formatMessage({ id: "Enter response" })}
          />
        </Form.Item>
        <Form.Item className="lastItem" id="thePopupContainer">
          <div></div>
          <Space direction="vertical">
            <Space align="end">
              <Button disabled={!this.state.passwordEditing} onClick={this.onCancel}>
                <div className="text-capitalize-first">
                  <FormattedMessage id="cancel" />
                </div>
              </Button>
              <Button disabled={!this.state.filled} type="primary" onClick={this.onSubmit}>
                <div className="text-capitalize-first">
                  <FormattedMessage id="update" />
                </div>
              </Button>
            </Space>
          </Space>
        </Form.Item>
      </>
    );
  };

  render() {
    return (
      <Wrapper>
        <Spin spinning={this.state.saving}>
          <Form ref={this.passwordChange} layout="vertical" onFieldsChange={this.checkAllFields} requiredMark={false}>
            <div className="stepHeader">
              <Avatar size="small" shape="circle">
                1
              </Avatar>
              <div>
                <FormattedMessage id="Update Password" />
              </div>
            </div>
            <Form.Item
              name="current_password"
              label={
                <span className="text-capitalize">
                  <FormattedMessage id="current" />
                </span>
              }
              rules={[
                {
                  required: true,
                  message: <div>{this.props.intl.formatMessage({ id: "enter current password" })}</div>,
                },
              ]}
            >
              <Input.Password placeholder={this.props.intl.formatMessage({ id: "Enter current password" })} />
            </Form.Item>
            <Form.Item
              name="new_password"
              label={
                <span className="text-capitalize">
                  <FormattedMessage id="new" />
                </span>
              }
              rules={[
                {
                  required: true,
                  message: (
                    <div className="text-capitalize-first">
                      {this.props.intl.formatMessage({ id: "enter valid password" })}
                    </div>
                  ),
                  validator: (rule, value) => this.checkPassword(rule, value),
                },
              ]}
            >
              <Input.Password
                onChange={e => this.setState({ newPass: e?.target?.value ?? "" })}
                placeholder={this.props.intl.formatMessage({ id: "Enter new password" })}
              />
            </Form.Item>
            <div className={`passwordValidatorContainer ${this.state.newPass !== "" ? "" : "hide-div"}`}>
              <div className="validationPassword">
                <PasswordCriteria
                  password={this.state.newPass}
                  confirm_password={this.state.confirmPass}
                  show_confirm={true}
                />
              </div>
              <div></div>
            </div>
            <Form.Item
              name="confirm_new_password"
              label={
                <span className="text-capitalize">
                  <FormattedMessage id="confirm" />
                </span>
              }
              rules={[
                {
                  required: true,
                  message: (
                    <div className="text-capitalize-first">
                      {this.props.intl.formatMessage({ id: "passwords do not match" })}
                    </div>
                  ),
                  validator: (rule, value) => this.comparePasswords(rule, value),
                },
              ]}
            >
              <Input.Password
                onChange={e => this.setState({ confirmPass: e?.target?.value ?? "" })}
                placeholder={this.props.intl.formatMessage({ id: "Confirm password" })}
              />
            </Form.Item>
            {this.state.show && (
              <>
                <div className="stepHeader step2">
                  <Avatar size="small" shape="circle">
                    2
                  </Avatar>
                  <div className="text-capitalize">
                    <FormattedMessage id="authorise" />
                  </div>
                </div>
                {this.getSecurityQuestions()}
              </>
            )}
          </Form>
        </Spin>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  .ant-form-item-label > label {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-right: 1em;
  }
  .ant-form-item {
    margin: 1em 0;
    width: 100%;
    max-width: 308px;
    margin-left: 40px;
    .ant-form-item-label {
      width: 280px;
      text-align: left;
    }
  }
  .ant-tooltip-disabled-compatible-wrapper {
    width: 100%;
  }
  .validationPassword {
    margin: 1em;
  }
  .lastItem {
    padding-top: 24px;
    .ant-form-item-control-input-content {
      display: grid;
      grid-template-columns: 0px auto;
      .ant-popover-buttons {
        .ant-btn {
          width: auto;
        }
      }
    }
  }
  .ant-avatar-circle {
    margin-right: 1.1em;
    background: ${props => props.theme.color.purple["purple-3"]};
  }
  .stepHeader {
    width: 100%;
    font-weight: ${props => props.theme.text.bold};
    margin-top: 1em;
    margin-bottom: 1em;
    display: flex;
  }
  .step2 {
    padding-top: 40px;
  }
  .passwordValidatorContainer {
    display: grid;
    grid-template-columns: auto 70px;
    margin-left: 30px;
    .validationPassword {
      display: grid;
    }
  }
  .hide-div {
    display: none;
  }

  .popconfirmSave {
    width: 100%;
  }
`;

export default withTheme(injectIntl(UpdatePassword));
